import { html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { LandiStorefrontElement } from '../base/landi-storefront-element.js';
import { sharedStyles, buttonStyles, inputStyles, layoutStyles, typographyStyles } from '../styles/shared.js';
import {
  cartState,
  storeApi,
  type Cart,
  type CheckoutCalculation,
  type PaymentMethodId,
  type PaymentSettings,
  formatPrice,
  getProductImageDisplay,
  resolveProductImageUrl,
  isValidEmail,
  resolveJjsProcessorSettings,
} from '@landi/store-core';

interface ShippingAddress {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  country: string;
}

interface ShippingOption {
  id: string;
  name: string;
  description: string;
  price: number;
  estimatedDays: string;
}

type CheckoutStep = 'shipping' | 'payment' | 'confirmation';

/**
 * Multi-step checkout component with shipping form and payment
 * Styled to match The Golden Peptides design
 * 
 * @element landi-checkout
 * @fires order-complete - Fired when order is completed successfully
 */
@customElement('landi-checkout')
export class LandiCheckout extends LandiStorefrontElement {
  static styles = [
    sharedStyles,
    buttonStyles,
    inputStyles,
    layoutStyles,
    typographyStyles,
    css`
      :host {
        display: block;
        min-height: 100vh;
        background: var(--landi-background);
      }
      
      .checkout-container {
        max-width: 80rem;
        margin: 0 auto;
        padding: 2rem 1.5rem;
      }
      
      @media (min-width: 640px) {
        .checkout-container {
          padding: 3rem;
        }
      }
      
      .checkout-header {
        text-align: center;
        margin-bottom: 2rem;
      }
      
      .checkout-title {
        font-family: var(--landi-font-serif);
        font-size: 2rem;
        font-weight: 500;
        color: var(--landi-text);
        margin: 0 0 1.5rem;
      }
      
      .steps-indicator {
        display: flex;
        justify-content: center;
        gap: 0;
        max-width: 600px;
        margin: 0 auto;
      }
      
      .step {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.75rem 1.5rem;
        font-size: 0.813rem;
        font-weight: 500;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        color: var(--landi-text-light);
        position: relative;
      }
      
      .step.active {
        color: var(--landi-primary);
      }
      
      .step.completed {
        color: var(--landi-success);
      }
      
      .step-number {
        width: 24px;
        height: 24px;
        border-radius: 50%;
        border: 2px solid currentColor;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 0.75rem;
        font-weight: 600;
      }
      
      .step.completed .step-number {
        background: var(--landi-success);
        border-color: var(--landi-success);
        color: white;
      }
      
      .step-connector {
        width: 60px;
        height: 2px;
        background: var(--landi-border);
      }
      
      .step.completed + .step-connector,
      .step.active + .step-connector {
        background: var(--landi-primary);
      }
      
      .checkout-layout {
        display: grid;
        grid-template-columns: 1fr;
        gap: 2rem;
      }
      
      @media (min-width: 1024px) {
        .checkout-layout {
          grid-template-columns: 1fr 380px;
        }
      }
      
      .checkout-form {
        background: var(--landi-surface);
        border-radius: var(--landi-radius-md);
        padding: 2rem;
        box-shadow: var(--landi-shadow);
      }
      
      .form-section {
        margin-bottom: 2rem;
      }
      
      .form-section:last-child {
        margin-bottom: 0;
      }
      
      .form-section-title {
        font-family: var(--landi-font-serif);
        font-size: 1.25rem;
        font-weight: 500;
        color: var(--landi-text);
        margin: 0 0 1.5rem;
        padding-bottom: 0.75rem;
        border-bottom: 1px solid var(--landi-border);
      }
      
      .form-grid {
        display: grid;
        grid-template-columns: 1fr;
        gap: 1rem;
      }
      
      @media (min-width: 640px) {
        .form-grid {
          grid-template-columns: repeat(2, 1fr);
        }
        
        .form-grid .full-width {
          grid-column: 1 / -1;
        }
      }
      
      .shipping-options {
        display: flex;
        flex-direction: column;
        gap: 0.75rem;
      }
      
      .shipping-option {
        display: flex;
        align-items: center;
        gap: 1rem;
        padding: 1rem;
        border: 1px solid var(--landi-border);
        border-radius: var(--landi-radius);
        cursor: pointer;
        transition: border-color 200ms ease;
      }
      
      .shipping-option:hover {
        border-color: var(--landi-primary);
      }
      
      .shipping-option.selected {
        border-color: var(--landi-primary);
        background: var(--landi-primary-light);
      }
      
      .shipping-option input[type="radio"] {
        width: 20px;
        height: 20px;
        accent-color: var(--landi-primary);
      }

      /* Eligibility / compliance section */
      .eligibility-section {
        margin-bottom: 2rem;
        padding: 1.5rem;
        background: var(--landi-background);
        border-radius: var(--landi-radius);
        border: 1px solid var(--landi-border);
      }
      .eligibility-section .checkbox-row {
        display: flex;
        align-items: flex-start;
        gap: 0.75rem;
        margin-bottom: 1rem;
      }
      .eligibility-section .checkbox-row:last-child {
        margin-bottom: 0;
      }
      .eligibility-section input[type="checkbox"] {
        width: 20px;
        height: 20px;
        margin-top: 0.2rem;
        flex-shrink: 0;
        accent-color: var(--landi-primary);
      }
      .eligibility-section .checkbox-label {
        font-size: 0.875rem;
        color: var(--landi-text);
        line-height: 1.4;
      }
      .eligibility-error {
        font-size: 0.813rem;
        color: var(--landi-error, #b91c1c);
        margin-top: 0.5rem;
      }
      
      .shipping-option-details {
        flex: 1;
      }
      
      .shipping-option-name {
        font-weight: 500;
        color: var(--landi-text);
        margin-bottom: 0.25rem;
      }
      
      .shipping-option-desc {
        font-size: 0.813rem;
        color: var(--landi-text-muted);
      }
      
      .shipping-option-price {
        font-weight: 600;
        color: var(--landi-text);
      }

      .payment-methods {
        display: flex;
        flex-direction: column;
        gap: 0.75rem;
        margin-bottom: 1.5rem;
      }

      .payment-method-option {
        display: flex;
        align-items: center;
        gap: 1rem;
        padding: 1rem;
        border: 1px solid var(--landi-border);
        border-radius: var(--landi-radius);
        cursor: pointer;
        transition: border-color 200ms ease;
      }

      .payment-method-option:hover {
        border-color: var(--landi-primary);
      }

      .payment-method-option.selected {
        border-color: var(--landi-primary);
        background: var(--landi-primary-light);
      }

      .payment-method-option input[type="radio"] {
        width: 20px;
        height: 20px;
        accent-color: var(--landi-primary);
      }

      .payment-method-label {
        flex: 1;
        font-weight: 500;
        color: var(--landi-text);
      }

      .payment-loading {
        font-size: 0.875rem;
        color: var(--landi-text-muted);
        padding: 1rem 0;
      }

      .payment-error {
        font-size: 0.813rem;
        color: var(--landi-error, #b91c1c);
        margin-top: 0.75rem;
      }
      
      /* Payment form */
      .payment-card {
        padding: 1.5rem;
        background: var(--landi-background);
        border-radius: var(--landi-radius);
        border: 1px solid var(--landi-border);
      }
      
      .card-icons {
        display: flex;
        gap: 0.5rem;
        margin-bottom: 1rem;
      }
      
      .card-icon {
        height: 24px;
        opacity: 0.6;
      }
      
      /* Order summary sidebar */
      .order-summary {
        position: sticky;
        top: 2rem;
        background: var(--landi-surface);
        border-radius: var(--landi-radius-md);
        padding: 1.5rem;
        box-shadow: var(--landi-shadow);
        height: fit-content;
      }
      
      .summary-title {
        font-family: var(--landi-font-serif);
        font-size: 1.125rem;
        font-weight: 500;
        color: var(--landi-text);
        margin: 0 0 1rem;
        padding-bottom: 0.75rem;
        border-bottom: 1px solid var(--landi-border);
      }
      
      .summary-items {
        margin-bottom: 1rem;
        max-height: 200px;
        overflow-y: auto;
      }
      
      .summary-item {
        display: flex;
        gap: 0.75rem;
        padding: 0.75rem 0;
        border-bottom: 1px solid var(--landi-border-light);
      }
      
      .summary-item:last-child {
        border-bottom: none;
      }
      
      .summary-item-image {
        width: 48px;
        height: 48px;
        object-fit: cover;
        border-radius: var(--landi-radius);
        background: var(--landi-background);
      }
      
      .summary-item-details {
        flex: 1;
        min-width: 0;
      }
      
      .summary-item-name {
        font-size: 0.875rem;
        font-weight: 500;
        color: var(--landi-text);
        margin: 0 0 0.25rem;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      
      .summary-item-qty {
        font-size: 0.75rem;
        color: var(--landi-text-muted);
      }
      
      .summary-item-price {
        font-size: 0.875rem;
        font-weight: 500;
        color: var(--landi-text);
      }
      
      .summary-rows {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
        padding: 1rem 0;
        border-top: 1px solid var(--landi-border);
      }
      
      .summary-row {
        display: flex;
        justify-content: space-between;
        font-size: 0.875rem;
      }
      
      .summary-row.total {
        padding-top: 0.75rem;
        border-top: 1px solid var(--landi-border);
        font-weight: 600;
        font-size: 1rem;
      }
      
      /* Confirmation */
      .confirmation {
        text-align: center;
        padding: 3rem 2rem;
      }
      
      .confirmation-icon {
        width: 80px;
        height: 80px;
        margin: 0 auto 1.5rem;
        background: var(--landi-success-bg);
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        color: var(--landi-success);
      }
      
      .confirmation h2 {
        font-family: var(--landi-font-serif);
        font-size: 1.75rem;
        color: var(--landi-text);
        margin: 0 0 0.5rem;
      }
      
      .confirmation p {
        color: var(--landi-text-muted);
        margin: 0 0 1.5rem;
      }
      
      .order-number {
        display: inline-block;
        padding: 0.5rem 1rem;
        background: var(--landi-background);
        border-radius: var(--landi-radius);
        font-family: monospace;
        font-size: 0.875rem;
        color: var(--landi-text);
        margin-bottom: 2rem;
      }
      
      .form-actions {
        display: flex;
        gap: 1rem;
        margin-top: 2rem;
      }
      
      .form-actions .btn {
        flex: 1;
      }
    `,
  ];

  @property({ type: String, attribute: 'success-href' }) successHref = 'index.html';
  @property({ type: String, attribute: 'cart-href' }) cartHref = 'cart.html';

  @state() private cart: Cart = { items: [], subtotal: 0, tax: 0, shipping: 0, discount: 0, total: 0, itemCount: 0 };
  @state() private apiTotals: CheckoutCalculation | null = null;
  @state() private currentStep: CheckoutStep = 'shipping';
  @state() private orderId = '';
  
  @state() private shippingAddress: ShippingAddress = {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    zip: '',
    country: 'US',
  };
  
  @state() private selectedShipping = '';
  @state() private errors: Record<string, string> = {};
  @state() private paymentSettings: PaymentSettings | null = null;
  @state() private paymentSettingsLoading = false;
  @state() private paymentSettingsError = '';
  @state() private selectedPaymentMethod: PaymentMethodId = 'jjs';
  @state() private jjsPaymentLoading = false;
  @state() private jjsPaymentError = '';
  @state() private orderSubmitting = false;

  /** Compliance: age gate (required before proceeding) */
  @state() private ageConfirmed = false;
  /** Compliance: research-use-only attestation */
  @state() private attestationResearch = false;
  /** Compliance: no diagnostic/therapeutic use attestation */
  @state() private attestationNoDiagnostic = false;
  /** Compliance: laws and regulations attestation */
  @state() private attestationLaws = false;

  private shippingOptions: ShippingOption[] = [
    { id: 'standard', name: 'Standard Shipping', description: '5-7 business days', price: 5.99, estimatedDays: '5-7' },
    { id: 'express', name: 'Express Shipping', description: '2-3 business days', price: 12.99, estimatedDays: '2-3' },
    { id: 'overnight', name: 'Overnight Shipping', description: 'Next business day', price: 24.99, estimatedDays: '1' },
  ];

  private unsubscribe?: () => void;

  connectedCallback() {
    super.connectedCallback();
    this.restoreConfirmationFromUrl();
    this.unsubscribe = cartState.subscribe((cart) => {
      this.cart = cart;
      void this.refreshApiTotals();
    });
    this.selectedShipping = this.shippingOptions[0].id;
    void this.refreshApiTotals();
    void this.loadPaymentSettings();
  }

  private restoreConfirmationFromUrl(): void {
    const params = new URLSearchParams(window.location.search);
    const returnOrderId = params.get('orderId') ?? params.get('order');
    if (!returnOrderId) {
      return;
    }
    this.orderId = returnOrderId;
    this.currentStep = 'confirmation';
  }

  private async loadPaymentSettings(): Promise<void> {
    this.paymentSettingsLoading = true;
    this.paymentSettingsError = '';
    try {
      const settings = await storeApi.getPaymentSettings();
      this.paymentSettings = settings;
      const methods = this.checkoutPaymentMethods;
      if (methods.length === 0) {
        this.paymentSettingsError = 'No payment methods are configured for this store.';
        return;
      }
      if (!methods.includes(this.selectedPaymentMethod)) {
        this.selectedPaymentMethod = methods[0];
      }
    } catch (err) {
      this.paymentSettingsError =
        err instanceof Error ? err.message : 'Failed to load payment settings';
    } finally {
      this.paymentSettingsLoading = false;
    }
  }

  private async refreshApiTotals(): Promise<void> {
    if (!storeApi.isApiMode() || this.cart.items.length === 0) {
      this.apiTotals = null;
      return;
    }
    try {
      this.apiTotals = await storeApi.checkoutCalculate({
        items: this.cart.items,
        couponCode: this.cart.couponCode,
        shippingAddress: this.shippingAddress,
        shippingMethodId: this.selectedShipping,
      });
    } catch {
      this.apiTotals = null;
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.unsubscribe?.();
  }

  private get selectedShippingOption(): ShippingOption | undefined {
    return this.shippingOptions.find(o => o.id === this.selectedShipping);
  }

  private get orderTotal(): number {
    if (this.apiTotals) {
      return this.apiTotals.total;
    }
    const shipping = this.selectedShippingOption?.price || 0;
    return this.cart.subtotal + this.cart.tax + shipping - this.cart.discount;
  }

  private get displaySubtotal(): number {
    return this.apiTotals?.subtotal ?? this.cart.subtotal;
  }

  private get displayTax(): number {
    return this.apiTotals?.tax ?? this.cart.tax;
  }

  private get displayShipping(): number {
    return this.apiTotals?.shipping ?? (this.selectedShippingOption?.price || 0);
  }

  private get displayDiscount(): number {
    return this.apiTotals?.discount ?? this.cart.discount;
  }

  private get eligibilitySatisfied(): boolean {
    return this.ageConfirmed && this.attestationResearch && this.attestationNoDiagnostic && this.attestationLaws;
  }

  private get checkoutPaymentMethods(): PaymentMethodId[] {
    const settings = this.paymentSettings;
    if (!settings) {
      return [];
    }
    return (settings.enabledMethods ?? []).filter((method) => {
      if (method === 'jjs') {
        const jjs = resolveJjsProcessorSettings(settings.processorConfig);
        return jjs.enabled;
      }
      return true;
    });
  }

  private get showPaymentMethodPicker(): boolean {
    return this.checkoutPaymentMethods.length > 1;
  }

  private get jjsSettings() {
    return resolveJjsProcessorSettings(this.paymentSettings?.processorConfig);
  }

  private getPaymentMethodLabel(method: PaymentMethodId): string {
    if (method === 'jjs') {
      return this.jjsSettings.title;
    }
    const labels: Record<Exclude<PaymentMethodId, 'jjs'>, string> = {
      card: 'Credit / Debit Card',
      stripe: 'Stripe',
      paypal: 'PayPal',
      manual: 'Manual Payment',
    };
    return labels[method];
  }

  private validateShipping(): boolean {
    const errors: Record<string, string> = {};
    if (!this.eligibilitySatisfied) errors.eligibility = 'You must confirm age and accept all attestations to continue.';
    if (!this.shippingAddress.firstName.trim()) errors.firstName = 'First name is required';
    if (!this.shippingAddress.lastName.trim()) errors.lastName = 'Last name is required';
    if (!this.shippingAddress.email.trim()) errors.email = 'Email is required';
    else if (!isValidEmail(this.shippingAddress.email)) errors.email = 'Invalid email address';
    if (!this.shippingAddress.address1.trim()) errors.address1 = 'Address is required';
    if (!this.shippingAddress.city.trim()) errors.city = 'City is required';
    if (!this.shippingAddress.state.trim()) errors.state = 'State is required';
    if (!this.shippingAddress.zip.trim()) errors.zip = 'ZIP code is required';
    this.errors = errors;
    return Object.keys(errors).length === 0;
  }

  private handleInputChange(field: keyof ShippingAddress, value: string) {
    this.shippingAddress = { ...this.shippingAddress, [field]: value };
    // Clear error when user types
    if (this.errors[field]) {
      const { [field]: _, ...rest } = this.errors;
      this.errors = rest;
    }
  }

  private clearEligibilityError() {
    if (this.errors.eligibility) {
      const { eligibility: _, ...rest } = this.errors;
      this.errors = rest;
    }
  }

  private async proceedToPayment(): Promise<void> {
    if (!this.validateShipping()) {
      return;
    }
    await this.loadPaymentSettings();
    this.currentStep = 'payment';
  }

  private goBack() {
    if (this.currentStep === 'payment') {
      this.currentStep = 'shipping';
    }
  }

  private async placeOrder(): Promise<void> {
    const cart = cartState.getCart();
    this.orderSubmitting = true;
    this.errors = { ...this.errors, submit: '' };
    try {
      if (storeApi.isApiMode()) {
        const result = await storeApi.checkoutProcess({
          items: cart.items,
          couponCode: cart.couponCode,
          shippingAddress: this.shippingAddress,
          shippingMethod: this.selectedShipping,
          email: this.shippingAddress.email,
        });
        this.orderId = result.orderId;
      } else {
        this.orderId = `ORD-${Date.now().toString(36).toUpperCase()}`;
      }
      this.currentStep = 'confirmation';
      cartState.clearCart();
      this.dispatchEvent(new CustomEvent('order-complete', {
        bubbles: true,
        composed: true,
        detail: { orderId: this.orderId },
      }));
    } catch (err) {
      this.errors = {
        ...this.errors,
        submit: err instanceof Error ? err.message : 'Order failed',
      };
    } finally {
      this.orderSubmitting = false;
    }
  }

  private renderSteps() {
    const steps: { key: CheckoutStep; label: string }[] = [
      { key: 'shipping', label: 'Shipping' },
      { key: 'payment', label: 'Payment' },
      { key: 'confirmation', label: 'Confirmation' },
    ];
    
    const currentIndex = steps.findIndex(s => s.key === this.currentStep);
    
    return html`
      <div class="steps-indicator">
        ${steps.map((step, index) => html`
          ${index > 0 ? html`<div class="step-connector"></div>` : ''}
          <div class="step ${index < currentIndex ? 'completed' : ''} ${index === currentIndex ? 'active' : ''}">
            <span class="step-number">
              ${index < currentIndex ? '✓' : index + 1}
            </span>
            <span class="step-label">${step.label}</span>
          </div>
        `)}
      </div>
    `;
  }

  private renderShippingForm() {
    return html`
      <div class="form-section eligibility-section">
        <h3 class="form-section-title">Eligibility &amp; Attestations</h3>
        <p class="checkbox-label" style="margin-bottom: 1rem;">You must confirm the following before proceeding.</p>
        <label class="checkbox-row">
          <input
            type="checkbox"
            .checked=${this.ageConfirmed}
            @change=${(e: Event) => { this.ageConfirmed = (e.target as HTMLInputElement).checked; this.clearEligibilityError(); }}
            aria-required="true"
          />
          <span class="checkbox-label">I confirm I am 18 years or older.</span>
        </label>
        <label class="checkbox-row">
          <input
            type="checkbox"
            .checked=${this.attestationResearch}
            @change=${(e: Event) => { this.attestationResearch = (e.target as HTMLInputElement).checked; this.clearEligibilityError(); }}
            aria-required="true"
          />
          <span class="checkbox-label">I acknowledge these products are for research use only and not for human consumption.</span>
        </label>
        <label class="checkbox-row">
          <input
            type="checkbox"
            .checked=${this.attestationNoDiagnostic}
            @change=${(e: Event) => { this.attestationNoDiagnostic = (e.target as HTMLInputElement).checked; this.clearEligibilityError(); }}
            aria-required="true"
          />
          <span class="checkbox-label">I agree not to use these products for diagnostic, therapeutic, or clinical purposes.</span>
        </label>
        <label class="checkbox-row">
          <input
            type="checkbox"
            .checked=${this.attestationLaws}
            @change=${(e: Event) => { this.attestationLaws = (e.target as HTMLInputElement).checked; this.clearEligibilityError(); }}
            aria-required="true"
          />
          <span class="checkbox-label">I confirm I will comply with all applicable laws and regulations.</span>
        </label>
        ${this.errors.eligibility ? html`<p class="eligibility-error">${this.errors.eligibility}</p>` : ''}
      </div>

      <div class="form-section">
        <h3 class="form-section-title">Contact Information</h3>
        <div class="form-grid">
          <div class="input-group">
            <label class="input-label">First Name *</label>
            <input 
              type="text" 
              class="input ${this.errors.firstName ? 'input-error' : ''}"
              .value=${this.shippingAddress.firstName}
              @input=${(e: Event) => this.handleInputChange('firstName', (e.target as HTMLInputElement).value)}
            />
            ${this.errors.firstName ? html`<span class="error-text">${this.errors.firstName}</span>` : ''}
          </div>
          <div class="input-group">
            <label class="input-label">Last Name *</label>
            <input 
              type="text" 
              class="input ${this.errors.lastName ? 'input-error' : ''}"
              .value=${this.shippingAddress.lastName}
              @input=${(e: Event) => this.handleInputChange('lastName', (e.target as HTMLInputElement).value)}
            />
            ${this.errors.lastName ? html`<span class="error-text">${this.errors.lastName}</span>` : ''}
          </div>
          <div class="input-group">
            <label class="input-label">Email *</label>
            <input 
              type="email" 
              class="input ${this.errors.email ? 'input-error' : ''}"
              .value=${this.shippingAddress.email}
              @input=${(e: Event) => this.handleInputChange('email', (e.target as HTMLInputElement).value)}
            />
            ${this.errors.email ? html`<span class="error-text">${this.errors.email}</span>` : ''}
          </div>
          <div class="input-group">
            <label class="input-label">Phone</label>
            <input 
              type="tel" 
              class="input"
              .value=${this.shippingAddress.phone}
              @input=${(e: Event) => this.handleInputChange('phone', (e.target as HTMLInputElement).value)}
            />
          </div>
        </div>
      </div>
      
      <div class="form-section">
        <h3 class="form-section-title">Shipping Address</h3>
        <div class="form-grid">
          <div class="input-group full-width">
            <label class="input-label">Street Address *</label>
            <input 
              type="text" 
              class="input ${this.errors.address1 ? 'input-error' : ''}"
              .value=${this.shippingAddress.address1}
              @input=${(e: Event) => this.handleInputChange('address1', (e.target as HTMLInputElement).value)}
            />
            ${this.errors.address1 ? html`<span class="error-text">${this.errors.address1}</span>` : ''}
          </div>
          <div class="input-group full-width">
            <label class="input-label">Apartment, suite, etc.</label>
            <input 
              type="text" 
              class="input"
              .value=${this.shippingAddress.address2}
              @input=${(e: Event) => this.handleInputChange('address2', (e.target as HTMLInputElement).value)}
            />
          </div>
          <div class="input-group">
            <label class="input-label">City *</label>
            <input 
              type="text" 
              class="input ${this.errors.city ? 'input-error' : ''}"
              .value=${this.shippingAddress.city}
              @input=${(e: Event) => this.handleInputChange('city', (e.target as HTMLInputElement).value)}
            />
            ${this.errors.city ? html`<span class="error-text">${this.errors.city}</span>` : ''}
          </div>
          <div class="input-group">
            <label class="input-label">State *</label>
            <input 
              type="text" 
              class="input ${this.errors.state ? 'input-error' : ''}"
              .value=${this.shippingAddress.state}
              @input=${(e: Event) => this.handleInputChange('state', (e.target as HTMLInputElement).value)}
            />
            ${this.errors.state ? html`<span class="error-text">${this.errors.state}</span>` : ''}
          </div>
          <div class="input-group">
            <label class="input-label">ZIP Code *</label>
            <input 
              type="text" 
              class="input ${this.errors.zip ? 'input-error' : ''}"
              .value=${this.shippingAddress.zip}
              @input=${(e: Event) => this.handleInputChange('zip', (e.target as HTMLInputElement).value)}
            />
            ${this.errors.zip ? html`<span class="error-text">${this.errors.zip}</span>` : ''}
          </div>
        </div>
      </div>
      
      <div class="form-section">
        <h3 class="form-section-title">Shipping Method</h3>
        <div class="shipping-options">
          ${this.shippingOptions.map(option => html`
            <label class="shipping-option ${this.selectedShipping === option.id ? 'selected' : ''}">
              <input 
                type="radio" 
                name="shipping" 
                .value=${option.id}
                .checked=${this.selectedShipping === option.id}
                @change=${() => {
                  this.selectedShipping = option.id;
                  void this.refreshApiTotals();
                }}
              />
              <div class="shipping-option-details">
                <div class="shipping-option-name">${option.name}</div>
                <div class="shipping-option-desc">${option.description}</div>
              </div>
              <span class="shipping-option-price">${formatPrice(option.price)}</span>
            </label>
          `)}
        </div>
      </div>
      
      <div class="form-actions">
        <a href="${this.cartHref}" class="btn btn-secondary">Back to Cart</a>
        <button class="btn btn-primary" @click=${this.proceedToPayment}>
          Continue to Payment
        </button>
      </div>
    `;
  }

  private async handlePaymentRedirect(): Promise<void> {
    const cart = cartState.getCart();
    this.jjsPaymentLoading = true;
    this.jjsPaymentError = '';
    try {
      const result = await storeApi.initJjsPayment({
        items: cart.items,
        shippingAddress: this.shippingAddress,
        shippingMethod: this.selectedShipping,
        email: this.shippingAddress.email,
        couponCode: cart.couponCode,
      });
      window.location.href = result.redirectUrl;
    } catch (err) {
      this.jjsPaymentError =
        err instanceof Error ? err.message : 'Payment initialization failed';
      this.jjsPaymentLoading = false;
    }
  }

  private selectPaymentMethod(method: PaymentMethodId): void {
    this.selectedPaymentMethod = method;
    this.jjsPaymentError = '';
    this.errors = { ...this.errors, submit: '' };
  }

  private renderPaymentMethodPicker() {
    return html`
      <div class="form-section">
        <h3 class="form-section-title">Payment Method</h3>
        <div class="payment-methods">
          ${this.checkoutPaymentMethods.map((method) => html`
            <label class="payment-method-option ${this.selectedPaymentMethod === method ? 'selected' : ''}">
              <input
                type="radio"
                name="payment-method"
                .value=${method}
                .checked=${this.selectedPaymentMethod === method}
                @change=${() => this.selectPaymentMethod(method)}
              />
              <span class="payment-method-label">${this.getPaymentMethodLabel(method)}</span>
            </label>
          `)}
        </div>
      </div>
    `;
  }

  private renderJjsPaymentPanel() {
    const jjs = this.jjsSettings;
    return html`
      <div class="form-section">
        <h3 class="form-section-title" style="font-size: 1.125rem; font-weight: 600; color: var(--landi-primary, #b4932a); margin-bottom: 1rem;">
          ${jjs.title}
        </h3>
        <div class="payment-card" style="background: rgba(180, 147, 42, 0.04); border: 1px solid rgba(180, 147, 42, 0.2); padding: 1.5rem; border-radius: 8px;">
          <p style="font-size: 0.875rem; line-height: 1.6; color: var(--landi-text-muted, #78716c); margin: 0;">
            ${jjs.description}
            <strong> After payment, you will not return to our website.</strong>
            A confirmation email with your invoice and order details will be sent upon successful checkout.
          </p>
        </div>
        <p style="font-size: 0.75rem; color: var(--landi-text-muted); margin-top: 1rem; text-align: center;">
          Your transaction is handled on a secure external portal
        </p>
      </div>
    `;
  }

  private renderCardPaymentPanel() {
    return html`
      <div class="form-section">
        <h3 class="form-section-title">Card Details</h3>
        <div class="payment-card">
          <div class="card-icons" aria-hidden="true">
            <span class="card-icon">VISA</span>
            <span class="card-icon">MC</span>
            <span class="card-icon">AMEX</span>
          </div>
          <div class="form-grid">
            <div class="input-group full-width">
              <label class="input-label">Card Number</label>
              <input type="text" class="input" placeholder="4242 4242 4242 4242" autocomplete="cc-number" />
            </div>
            <div class="input-group">
              <label class="input-label">Expiry</label>
              <input type="text" class="input" placeholder="MM/YY" autocomplete="cc-exp" />
            </div>
            <div class="input-group">
              <label class="input-label">CVC</label>
              <input type="text" class="input" placeholder="123" autocomplete="cc-csc" />
            </div>
          </div>
        </div>
      </div>
    `;
  }

  private renderPayPalPaymentPanel() {
    return html`
      <div class="form-section">
        <h3 class="form-section-title">PayPal</h3>
        <div class="payment-card">
          <p style="font-size: 0.875rem; line-height: 1.6; color: var(--landi-text-muted); margin: 0;">
            You will complete payment through PayPal after placing your order. A confirmation email will be sent once payment is received.
          </p>
        </div>
      </div>
    `;
  }

  private renderManualPaymentPanel() {
    return html`
      <div class="form-section">
        <h3 class="form-section-title">Manual Payment</h3>
        <div class="payment-card">
          <p style="font-size: 0.875rem; line-height: 1.6; color: var(--landi-text-muted); margin: 0;">
            Place your order now and we will email manual payment instructions. Your order will be processed after payment is confirmed.
          </p>
        </div>
      </div>
    `;
  }

  private renderSelectedPaymentPanel() {
    switch (this.selectedPaymentMethod) {
      case 'jjs':
        return this.renderJjsPaymentPanel();
      case 'card':
      case 'stripe':
        return this.renderCardPaymentPanel();
      case 'paypal':
        return this.renderPayPalPaymentPanel();
      case 'manual':
        return this.renderManualPaymentPanel();
      default:
        return html``;
    }
  }

  private renderPaymentActions() {
    const isJjs = this.selectedPaymentMethod === 'jjs';
    const busy = this.jjsPaymentLoading || this.orderSubmitting;

    return html`
      <div class="form-actions">
        <button class="btn btn-secondary" @click=${this.goBack} ?disabled=${busy}>
          Back
        </button>
        ${isJjs ? html`
          <button
            class="btn btn-primary"
            @click=${this.handlePaymentRedirect}
            ?disabled=${this.jjsPaymentLoading}
          >
            ${this.jjsPaymentLoading
              ? 'Redirecting...'
              : `Redirect to Secure Payment Portal - ${formatPrice(this.orderTotal)}`}
          </button>
        ` : html`
          <button class="btn btn-primary" @click=${this.placeOrder} ?disabled=${this.orderSubmitting}>
            ${this.orderSubmitting
              ? 'Processing...'
              : `Place Order - ${formatPrice(this.orderTotal)}`}
          </button>
        `}
      </div>
      ${this.jjsPaymentError ? html`<p class="payment-error">${this.jjsPaymentError}</p>` : ''}
      ${this.errors.submit ? html`<p class="payment-error">${this.errors.submit}</p>` : ''}
    `;
  }

  private renderPaymentForm() {
    if (this.paymentSettingsLoading) {
      return html`<p class="payment-loading">Loading payment options...</p>`;
    }

    if (this.paymentSettingsError) {
      return html`
        <p class="payment-error">${this.paymentSettingsError}</p>
        <div class="form-actions">
          <button class="btn btn-secondary" @click=${this.goBack}>Back</button>
        </div>
      `;
    }

    return html`
      ${this.showPaymentMethodPicker ? this.renderPaymentMethodPicker() : ''}
      ${this.renderSelectedPaymentPanel()}
      ${this.renderPaymentActions()}
    `;
  }

  private renderConfirmation() {
    return html`
      <div class="confirmation">
        <div class="confirmation-icon">
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="20 6 9 17 4 12"></polyline>
          </svg>
        </div>
        <h2>Thank You for Your Order!</h2>
        <p>We've received your order and will process it shortly.</p>
        <div class="order-number">Order #${this.orderId}</div>
        <p style="font-size: 0.875rem;">
          ${this.shippingAddress.email
            ? `A confirmation email has been sent to ${this.shippingAddress.email}.`
            : 'A confirmation email will be sent with your order details.'}
        </p>
        <a href=${this.successHref} class="btn btn-primary btn-lg">
          Continue Shopping
        </a>
      </div>
    `;
  }

  private renderOrderSummary() {
    const shipping = this.displayShipping;
    
    return html`
      <aside class="order-summary">
        <h2 class="summary-title">Order Summary</h2>
        
        <div class="summary-items">
          ${this.cart.items.map(item => {
            const imageDisplay = getProductImageDisplay({
              url: resolveProductImageUrl(item.image),
              name: item.name,
              sku: item.sku,
              theme: 'light',
            });
            return html`
            <div class="summary-item">
              <img 
                class="summary-item-image" 
                src=${imageDisplay.src}
                alt=""
                .onerror=${imageDisplay.onError}
              />
              <div class="summary-item-details">
                <p class="summary-item-name">${item.name}</p>
                <p class="summary-item-qty">Qty: ${item.quantity}</p>
              </div>
              <span class="summary-item-price">${formatPrice(item.price * item.quantity)}</span>
            </div>
          `;
          })}
        </div>
        
        <div class="summary-rows">
          <div class="summary-row">
            <span>Subtotal</span>
            <span>${formatPrice(this.displaySubtotal)}</span>
          </div>
          <div class="summary-row">
            <span>Shipping</span>
            <span>${formatPrice(shipping)}</span>
          </div>
          <div class="summary-row">
            <span>Tax</span>
            <span>${formatPrice(this.displayTax)}</span>
          </div>
          ${this.displayDiscount > 0 ? html`
            <div class="summary-row" style="color: var(--landi-success);">
              <span>Discount</span>
              <span>-${formatPrice(this.displayDiscount)}</span>
            </div>
          ` : ''}
          <div class="summary-row total">
            <span>Total</span>
            <span>${formatPrice(this.orderTotal)}</span>
          </div>
        </div>
      </aside>
    `;
  }

  render() {
    return html`
      <div class="checkout-container">
        <header class="checkout-header">
          <h1 class="checkout-title">Checkout</h1>
          ${this.renderSteps()}
        </header>
        
        ${this.currentStep === 'confirmation' ? html`
          <div class="checkout-form">
            ${this.renderConfirmation()}
          </div>
        ` : html`
          <div class="checkout-layout">
            <div class="checkout-form">
              ${this.currentStep === 'shipping' ? this.renderShippingForm() : ''}
              ${this.currentStep === 'payment' ? this.renderPaymentForm() : ''}
            </div>
            ${this.renderOrderSummary()}
          </div>
        `}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'landi-checkout': LandiCheckout;
  }
}
